<script>
  import { onMount, onDestroy, createEventDispatcher } from 'svelte';
  import { getCurrentUserStore } from '@stores/current_user';
  import Notification from '@utils/notification';
  import Profile from '@utils/profile';
  import Modal from '@components/Modal.svelte';
  import Input from '@components/Input.svelte';
  import Button from '@components/Button.svelte';

  import { sortBy } from 'lodash-es';
  import Fuse from 'fuse.js';

  import { getProfileStore } from '@stores/profile';
  let groupsStore = getProfileStore('groupsStore');
  let groupCollectionStore = getProfileStore('groupCollectionStore');
  let groupCollectionSortOrderStore = getProfileStore('groupCollectionSortOrderStore');

  import GroupCollectionAjax from '@ajax/group_collection';
  import UserAjax from '@ajax/user';

  const dispatch = createEventDispatcher();

  export let groupId;
  export let groupCollectionId;
  export let mobile = false;
  export let showModal = false;

  let currentUser = getCurrentUserStore('default');
  let group = groupsStore.find(groupId);

  let sortGroupCollections = function (collections, sortOrder) {
    return sortBy(collections, (gc) => {
      var idx = sortOrder.indexOf(gc.id);
      if (idx == -1) {
        idx = 9999;
      }
      return idx;
    });
  };

  let groupCollections = sortGroupCollections(groupCollectionStore, groupCollectionSortOrderStore);

  let unsubGroup = group.subscribe((x) => (group = x));
  let unsubAuth = currentUser.subscribe((x) => (currentUser = x));

  let searched = false;
  let loading = false;
  let creatingCollection = false;
  let addNewCollectionModal = false;

  let fromCollection;
  let toCollection;

  let fromCollectionUndoGroupIds;
  let toCollectionUndoGroupIds;

  let groupCollectionSearchEl;
  let groupCollectionInputEl;

  let bindWindowEvents = function () {
    window.undoCollectionMove = function () {
      if (fromCollection && toCollection && fromCollectionUndoGroupIds && toCollectionUndoGroupIds) {
        var data = [
          {
            collection_id: fromCollection.id,
            group_ids: fromCollectionUndoGroupIds,
          },
          {
            collection_id: toCollection.id,
            group_ids: toCollectionUndoGroupIds,
          },
        ];

        Notification.send({
          text: `<div class="bookmark-saved-notification"><img class='bookmark-saved-checkmark' src='/images/save-button/check.png' /><div class='main-text'>List move reverted</div></div>`,
          layout: mobile ? 'topCenter' : 'bottomLeft',
          timeout: 3500,
        });

        fromCollection.set({
          ...fromCollection,
          group_base_sort_order: fromCollectionUndoGroupIds,
        });

        toCollection.set({
          ...toCollection,
          group_base_sort_order: toCollectionUndoGroupIds,
        });

        GroupCollectionAjax.updateSortOrder(currentUser, data);
      }
    };
  };

  let fetchCollections = async function () {
    loading = groupCollections && groupCollections.length === 0;

    try {
      let resp = await GroupCollectionAjax.get(currentUser);
      groupCollections = sortGroupCollections(resp.group_collections, resp.group_collections_sort_order);
    } catch {}

    loading = false;
  };

  let searchGroupCollections = function () {
    if (groupCollectionSearchEl.value === '') {
      groupCollections = groupCollectionStore;
      searched = false;
      return;
    }

    let fuse = new Fuse(groupCollections, {
      keys: ['name'],
      threshold: 0.25,
    });

    groupCollections = fuse.search(groupCollectionSearchEl.value).map((x) => x.item);

    searched = true;
  };

  let createNewCollection = async function () {
    creatingCollection = true;

    try {
      let collection = await GroupCollectionAjax.create({
        name: groupCollectionInputEl.value,
      });

      let ids = [...Profile.collectionSortOrderByPageIds(), collection.id];
      groupCollectionSortOrderStore.set(ids);
      groupCollectionStore.add(collection);

      UserAjax.update(currentUser, {
        group_collection_sort_order: ids,
      });

      creatingCollection = false;
      selectGroupCollection(collection);
    } catch (err) {
      if (err && err.full_errors && err.full_errors.length > 0) {
        Notification.send({
          text: err.full_errors[0],
        });
      }
    }
  };

  let displayNotification = async function () {
    Notification.send({
      text: `<div class="bookmark-saved-notification"><img class='bookmark-saved-checkmark' src='/images/save-button/check.png' /><div class='main-text'>List moved to ${toCollection.name}</div><div class='actions'><span onclick='undoCollectionMove()'>Undo</span></div></div>`,
      layout: mobile ? 'topCenter' : 'bottomLeft',
      timeout: 3500,
    });
  };

  let hideModal = function () {
    fromCollection = undefined;
    toCollection = undefined;
    fromCollectionUndoGroupIds = undefined;
    toCollectionUndoGroupIds = undefined;
    searched = false;
    creatingCollection = false;
    hideAddNewCollectionModal();

    showModal = false;
    dispatch('close');
  };

  let selectGroupCollection = async function (gc) {
    fromCollection = groupCollectionStore.find(groupCollectionId);

    if (gc.id === groupCollectionId) {
      toCollection = groupCollectionStore.one({ default_collection: true });
    } else {
      toCollection = groupCollectionStore.find(gc.id);
    }

    fromCollectionUndoGroupIds = Profile.sortOrderByPageIds(groupCollectionId);
    toCollectionUndoGroupIds = Profile.sortOrderByPageIds(toCollection.id);

    let toCollectionGroupIds = [...toCollectionUndoGroupIds, groupId];
    let fromCollectionGroupIds = fromCollectionUndoGroupIds.filter((x) => x !== groupId);

    var data = [
      {
        collection_id: groupCollectionId,
        group_ids: fromCollectionGroupIds,
      },
      {
        collection_id: toCollection.id,
        group_ids: toCollectionGroupIds,
      },
    ];

    setTimeout(() => bindWindowEvents(), 0);
    displayNotification();

    fromCollection.set({
      ...fromCollection,
      group_base_sort_order: fromCollectionGroupIds,
    });

    toCollection.set({
      ...toCollection,
      group_base_sort_order: toCollectionGroupIds,
    });

    GroupCollectionAjax.updateSortOrder(currentUser, data);
  };

  let showAddNewCollectionModal = function () {
    addNewCollectionModal = true;
  };

  let hideAddNewCollectionModal = function () {
    addNewCollectionModal = false;
  };

  onMount(() => {
    fetchCollections();
  });

  onDestroy(() => {
    unsubGroup();
    unsubAuth();
  });
</script>

<style style="text/scss" lang="scss">
  .save-modal {
    margin-top: -30px;
    padding-left: 16px;
    padding-right: 16px;

    .save-modal-header {
      font-weight: 500;
      font-size: 24px;
      line-height: 32px;
      text-align: center;
    }

    .save-modal-subheader {
      font-size: 14px;
      line-height: 20px;
      opacity: 0.5;
      color: black;
      margin-top: 14px;
      text-align: center;
    }

    .search-search-bar {
      margin-top: 24px;
      position: relative;
    }

    .save-modal-lists-scrolled {
      padding-right: 30px !important;
    }

    .save-modal-lists {
      margin-top: 24px;
      max-height: 312px;
      overflow: auto;
      overflow-x: hidden;
      margin-left: -45px;
      margin-right: -45px;
      padding-left: 45px;
      padding-right: 45px;

      .save-modal-lists-label {
        font-weight: normal;
        font-size: 14px;
        line-height: 20px;
        color: #7f7f7f;
        margin-bottom: 12px;
      }
    }

    .add-new-section-container {
      margin-top: 14px;
      position: relative;
      margin-bottom: -28px;

      img {
        position: absolute;
        right: 15px;
        top: 15px;
        cursor: pointer;
      }
    }

    .save-modal-item-selected {
      background: #f0eaff;
    }

    .save-modal-item {
      border-radius: 12px;
      display: flex;
      height: 48px;
      align-items: center;
      padding-left: 20px;
      padding-right: 20px;
      cursor: pointer;
      margin-bottom: 8px;
      transition: 0.1s all;
      box-shadow: 0px 4px 14px rgba(0, 0, 0, 0.08);

      &:hover {
        box-shadow: 0px 2px 10px rgba(0, 0, 0, 0.2);
      }

      img {
        width: 24px;
        height: 24px;
        margin-right: 12px;
      }

      .save-modal-item-name {
        text-overflow: ellipsis;
        -webkit-line-clamp: 1;
        display: block;
        overflow: hidden;
        white-space: nowrap;
      }

      .save-modal-section-count {
        display: flex;
        align-items: center;

        span {
          font-size: 12px;
          line-height: 14px;
          opacity: 0.5;
          margin-top: 3px;
        }

        img {
          width: 16px;
          height: 16px;
          margin-right: 8px;
        }
      }

      span {
        font-weight: 500;
        font-size: 16px;
        line-height: 22px;
        flex: 1;
      }
    }

    .selected-group {
      background: #fff4f6;
      border: 2px solid #fff4f6;
      margin-top: 24px;
      cursor: unset;

      &:hover {
        border: 2px solid #fff4f6;
        box-shadow: unset;
      }

      .selected-group-cancel-btn {
        width: 40px;
        height: 40px;
        background: white;
        border-radius: 12px;
        align-items: center;
        justify-content: center;
        display: flex;
        margin-right: -13px;
        cursor: pointer;

        &:hover {
          box-shadow: 0px 2px 10px rgba(0, 0, 0, 0.08);
        }
      }
    }

    .create-new-button {
      border: 2px solid #ececec;
      border-radius: 12px;
      display: flex;
      height: 48px;
      align-items: center;
      padding-left: 20px;
      padding-right: 20px;
      cursor: pointer;
      transition: 0.1s all;
      margin-top: 15px;
      margin-bottom: -20px;

      &:hover {
        border: 2px solid transparent;
        box-shadow: 0px 2px 10px rgba(0, 0, 0, 0.2);
      }

      span {
        flex: 1;
        font-weight: 500;
        font-size: 16px;
        line-height: 22px;
        color: #a893f9;
      }
    }

    .group-emoji {
      width: 20px;
      height: 20px;
    }
  }

  .mobile {
    font-size: 14px !important;
  }

  .mobile-modal {
    padding-left: 0px !important;
    padding-right: 0px !important;

    .save-modal-lists {
      overflow: unset !important;
      overflow-x: unset !important;
      margin-left: unset !important;
      margin-right: unset !important;
      padding-left: unset !important;
      padding-right: unset !important;
      max-height: unset !important;
    }
  }

  * :global(.overlay-aligned-top) {
    align-items: flex-start !important;
    padding-top: calc(50vh - 320px);
  }
</style>

<div class="svelte-modal">
  <Modal
    style={mobile ? 'width:100%;' : 'width: 490px;'}
    mobile={mobile}
    overlayClass={mobile ? '' : 'overlay-aligned-top'}
    show={showModal}
    on:close={hideModal}>
    <div slot="content" class="save-modal" class:mobile-modal={mobile}>
      <div class="save-modal-header">Save this list to a collection</div>
      {#if loading}
        <div style="text-align: center; opacity: 0.7; color: #1a1a1a; margin-top: 35px;">
          <i class="fa fa-spin fa-spinner" />
          <span style="margin-left: 8px;">Loading</span>
        </div>
      {:else}
        {#if searched || (groupCollections && groupCollections.length > 1)}
          <div class="search-search-bar">
            <i class="fa fa-search" style="position: absolute; z-index: 1; top: 17px; left: 17px; color: #A893F9;" />
            <Input
              bind:inputEl={groupCollectionSearchEl}
              style="height: 48px;"
              inputStyle="padding-left: 44px;"
              placeholder="Find a collection"
              on:keyup={searchGroupCollections}
              focus={!mobile} />
          </div>
        {/if}
        <div
          class="save-modal-lists purple-scrollbar"
          class:save-modal-lists-scrolled={!mobile && groupCollections.length > 5}>
          <div class="save-modal-lists-label">Your collections</div>

          {#each groupCollections as gc (gc.id)}
            {#if !gc.default_collection}
              <div
                class="save-modal-item"
                class:save-modal-item-selected={groupCollectionId === gc.id}
                on:click|preventDefault|stopPropagation={() => selectGroupCollection(gc)}>
                <img src={gc.emoji.outside_src} alt="{gc.emoji.name} emoji" />
                <span class="save-modal-item-name">{gc.name}</span>
                <div class="save-modal-section-count">
                  <span>{gc.group_base_sort_order.length}</span>
                </div>
              </div>
            {/if}
          {/each}
        </div>
        <div
          class="create-new-button"
          on:click={() => {
            showAddNewCollectionModal();
          }}>
          <span>Create new collection</span>
          <img src="/images/save-button/plus.svg" />
        </div>
      {/if}
    </div>
  </Modal>

  <Modal
    on:close={() => {
      hideAddNewCollectionModal();
    }}
    style={mobile ? '' : 'width: 490px;'}
    mobile={mobile}
    bind:show={addNewCollectionModal}>
    <div slot="content" class="save-modal" class:mobile-modal={mobile}>
      <div class="save-modal-header">Save to a new collection</div>
      <div class="save-modal-subheader">Name your collection to save the item to it</div>
      <div style="margin-top: 40px; position: relative;">
        <img
          src="/emojis/purple_star.png"
          style="position: absolute; top: 14px; left: 20px; z-index: 1; width: 20px; height: 20px;" />
        <Input
          bind:inputEl={groupCollectionInputEl}
          placeholder="My new collection"
          inputStyle="padding-left: 52px;"
          on:enter={createNewCollection}
          focus={!mobile} />
      </div>
      <div style="margin-top: 30px; width: 170px; margin-left: auto; margin-right: auto;">
        <Button loading={creatingCollection} text="Create and save" on:click={createNewCollection} />
      </div>
    </div>
  </Modal>
</div>
